import { useState, useRef, useEffect } from 'react';
import { Box, Paper, Typography, TextField, IconButton, Chip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { knownDevices } from '../data/networkData';
import { mockSecurityData } from './DeviceDetail';

// ── Build context from device data ────────────────────────────

const hostnames = Array.from(new Set(Object.values(knownDevices).map(d => d.hostname)));
const deviceData = hostnames.map(h => ({ hostname: h, ...mockSecurityData(h) }));
const avgRisk = Math.round(deviceData.reduce((s, d) => s + d.riskScore, 0) / deviceData.length);
const critDevices = deviceData.filter(d => d.critical > 0).map(d => d.hostname);
const nonCompliantAV = deviceData.filter(d => d.avDaysAgo >= 2).map(d => d.hostname);
const lowStig = deviceData.filter(d => d.stigPct < 70).map(d => d.hostname);
const topRisk = [...deviceData].sort((a, b) => a.riskScore - b.riskScore).slice(0, 5);

// ── Mock response engine ───────────────────────────────────────

function generateResponse(query) {
  const q = query.toLowerCase();

  if (q.includes('critical') && (q.includes('vuln') || q.includes('cve'))) {
    return critDevices.length === 0
      ? 'No devices currently have critical vulnerabilities. System posture is clean.'
      : `**${critDevices.length} device(s) have critical vulnerabilities:**\n\n${critDevices.map(h => `• ${h}`).join('\n')}\n\nRecommendation: Prioritize patching these systems immediately. Review the Vulnerabilities page for CVE details.`;
  }

  if (q.includes('risk posture') || q.includes('overall risk') || q.includes('risk score')) {
    return `**Overall Risk Posture: ${avgRisk}/100**\n\nBreakdown:\n• High risk (below 70): ${deviceData.filter(d => d.riskScore < 70).length} devices\n• Moderate (70–84): ${deviceData.filter(d => d.riskScore >= 70 && d.riskScore < 85).length} devices\n• Low risk (85+): ${deviceData.filter(d => d.riskScore >= 85).length} devices\n\nThe network average of ${avgRisk} indicates ${avgRisk >= 85 ? 'a strong posture' : avgRisk >= 70 ? 'moderate risk — improvement needed' : 'elevated risk requiring immediate attention'}.`;
  }

  if (q.includes('non-compliant') || q.includes('c2c') || q.includes('ise')) {
    const nc = deviceData.filter(d => d.c2cStatus === 'Non-Compliant');
    return nc.length === 0
      ? 'All devices are currently ISE/C2C compliant.'
      : `**${nc.length} device(s) are non-compliant with C2C/ISE:**\n\n${nc.map(d => `• ${d.hostname}`).join('\n')}\n\nThese devices may be blocked from network access. Check the Comply to Connect page for details.`;
  }

  if (q.includes('antivirus') || q.includes('av') || q.includes('outdated')) {
    return nonCompliantAV.length === 0
      ? 'All devices have current AV definitions.'
      : `**${nonCompliantAV.length} device(s) have outdated AV definitions:**\n\n${nonCompliantAV.map(h => `• ${h}`).join('\n')}\n\nRecommendation: Force definition update via your AV management console.`;
  }

  if (q.includes('stig') || q.includes('compliance')) {
    return lowStig.length === 0
      ? 'All devices meet the 70% STIG compliance threshold.'
      : `**${lowStig.length} device(s) are below 70% STIG compliance:**\n\n${lowStig.map(h => {
          const d = deviceData.find(x => x.hostname === h);
          return `• ${h} — ${d?.stigPct}%`;
        }).join('\n')}\n\nReview the STIG Compliance page to prioritize remediation by CAT I findings.`;
  }

  if (q.includes('top') && q.includes('risk')) {
    return `**Top 5 Highest Risk Devices (lowest scores):**\n\n${topRisk.map((d, i) => `${i + 1}. ${d.hostname} — Score: ${d.riskScore}`).join('\n')}\n\nFocus remediation efforts on these devices first.`;
  }

  if (q.includes('offline') || q.includes('down')) {
    const offline = Object.values(knownDevices).filter(d => d.status === 'offline').map(d => d.hostname);
    const unique = Array.from(new Set(offline));
    return unique.length === 0
      ? 'All devices are currently online.'
      : `**${unique.length} device(s) appear offline:**\n\n${unique.map(h => `• ${h}`).join('\n')}`;
  }

  if (q.includes('executive') || q.includes('summary') || q.includes('report')) {
    return `**Executive Summary — ${new Date().toLocaleDateString()}**\n\nNetwork: Joint Operations Network\nTotal Devices: ${hostnames.length}\n\n**Risk Posture:** ${avgRisk}/100 (${avgRisk >= 85 ? 'LOW RISK' : avgRisk >= 70 ? 'MODERATE' : 'HIGH RISK'})\n**Critical Findings:** ${critDevices.length} devices with critical CVEs\n**AV Compliance:** ${hostnames.length - nonCompliantAV.length}/${hostnames.length} devices current\n**STIG Avg:** ${Math.round(deviceData.reduce((s, d) => s + d.stigPct, 0) / deviceData.length)}%\n**C2C Compliant:** ${deviceData.filter(d => d.c2cStatus === 'Compliant').length}/${hostnames.length}\n\nOverall network security posture requires attention in AV currency and STIG compliance.`;
  }

  return `I can help you analyze your network security posture. Try asking:\n\n• "Show all critical vulnerabilities"\n• "What is the overall risk posture?"\n• "Which devices are non-compliant?"\n• "List devices with outdated AV"\n• "What are the top 5 highest risk devices?"\n• "Generate executive summary"`;
}

// ── Suggestion Queries ─────────────────────────────────────────

const SUGGESTIONS = [
  'Show all critical vulnerabilities',
  'What is the overall risk posture?',
  'Which devices are non-compliant?',
  'List devices with outdated AV',
  'Summarize STIG compliance status',
  'Top 5 highest risk devices',
  'Which devices are offline?',
  'Generate executive summary',
];

// ── Message Bubble ─────────────────────────────────────────────

function Message({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom: 1.5 }}>
      <Box sx={{
        maxWidth: '75%',
        padding: '10px 14px',
        borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
        backgroundColor: isUser ? '#00bcd4' : '#111827',
        border: isUser ? 'none' : '1px solid #1e3a5f',
      }}>
        {msg.content.split('\n').map((line, i) => {
          const isBold = line.startsWith('**') && line.endsWith('**');
          return (
            <Typography key={i} sx={{
              color: isUser ? '#000000' : (isBold ? '#ffffff' : '#c0cfe0'),
              fontSize: 13,
              fontWeight: isBold ? 700 : 400,
              lineHeight: 1.6,
              fontFamily: line.startsWith('•') ? 'monospace' : 'inherit',
            }}>
              {isBold ? line.slice(2, -2) : line || '\u00A0'}
            </Typography>
          );
        })}
        <Typography sx={{ color: isUser ? '#006070' : '#4a5a70', fontSize: 10, marginTop: 0.5, textAlign: isUser ? 'right' : 'left' }}>
          {msg.time}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────

export default function AiAssistant() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hello. I'm ASPYN's AI assistant. I have full visibility into your network's security posture — ask me anything about vulnerabilities, compliance, risk scores, or device status.`,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    }
  ]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [history, setHistory] = useState([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  const send = (text) => {
    const q = text || input;
    if (!q.trim()) return;
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [...prev, { role: 'user', content: q, time }]);
    setHistory(prev => [q, ...prev.slice(0, 4)]);
    setInput('');
    setThinking(true);
    setTimeout(() => {
      setMessages(prev => [...prev, { role: 'assistant', content: generateResponse(q), time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
      setThinking(false);
    }, 800);
  };

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', height: 'calc(100vh - 64px)', padding: 3, display: 'flex', gap: 2, overflow: 'hidden' }}>

      {/* Left Panel */}
      <Box sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', minHeight: 0 }}>

        <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px' }}>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1.5 }}>Quick Queries</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {SUGGESTIONS.map(s => (
              <Box key={s} onClick={() => send(s)} sx={{
                padding: '8px 10px',
                borderRadius: 1,
                border: '1px solid #1e3a5f',
                cursor: 'pointer',
                '&:hover': { borderColor: '#00bcd4', backgroundColor: '#0d2137' },
              }}>
                <Typography sx={{ color: '#c0cfe0', fontSize: 12 }}>{s}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {history.length > 0 && (
          <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: '16px' }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1.25 }}>Recent</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {history.map((h, i) => (
                <Typography key={i} onClick={() => send(h)} sx={{
                  color: '#4a5a70', fontSize: 12, cursor: 'pointer', padding: '4px 6px', borderRadius: 1,
                  '&:hover': { color: '#00bcd4', backgroundColor: '#0d2137' },
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{h}</Typography>
              ))}
            </Box>
          </Paper>
        )}
      </Box>

      {/* Chat Area */}
      <Paper sx={{ flex: 1, minHeight: 0, backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <Box sx={{ padding: '14px 20px', borderBottom: '1px solid #1e3a5f', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4caf50' }} />
          <Typography sx={{ color: '#ffffff', fontWeight: 700 }}>ASPYN AI Assistant</Typography>
          <Chip label="Mock Mode" size="small" sx={{ backgroundColor: '#1e3a5f', color: '#7a8fa6', fontSize: 10, height: 18, marginLeft: 'auto' }} />
        </Box>

        {/* Messages */}
        <Box sx={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {messages.map((m, i) => <Message key={i} msg={m} />)}
          {thinking && (
            <Box sx={{ display: 'flex', gap: 0.5, padding: '10px 14px', width: 'fit-content' }}>
              {[0, 1, 2].map(i => (
                <Box key={i} sx={{
                  width: 7, height: 7, borderRadius: '50%', backgroundColor: '#1e3a5f',
                  animation: 'pulse 1.2s ease-in-out infinite',
                  animationDelay: `${i * 0.2}s`,
                  '@keyframes pulse': { '0%, 100%': { opacity: 0.3 }, '50%': { opacity: 1 } },
                }} />
              ))}
            </Box>
          )}
          <div ref={bottomRef} />
        </Box>

        {/* Input */}
        <Box sx={{ padding: '14px 20px', borderTop: '1px solid #1e3a5f', display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Ask about vulnerabilities, compliance, risk posture..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#0a1525',
                color: '#ffffff',
                '& fieldset': { borderColor: '#1e3a5f' },
                '&:hover fieldset': { borderColor: '#00bcd4' },
                '&.Mui-focused fieldset': { borderColor: '#00bcd4' },
              },
              '& input::placeholder': { color: '#4a5a70' },
            }}
          />
          <IconButton onClick={() => send()} disabled={!input.trim() || thinking} sx={{
            backgroundColor: '#00bcd4', color: '#000000', borderRadius: 1,
            '&:hover': { backgroundColor: '#00acc1' },
            '&.Mui-disabled': { backgroundColor: '#1e3a5f', color: '#4a5a70' },
          }}>
            <SendIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
}
